import {
  Button,
  Input,
  LegacyCard,
  LegacyForm,
  Modal,
  QRCode,
  Space,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { useState } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { userConfigurationApis } from "@payping/api";
import { useAppSelector } from "@/utils/redux";
import { GoogleLogo, Key, LockOutlined, Password, SafetyOutlined, UnlockOutlined } from "@payping/icons";

interface FormType {
  googleAuthCode: string;
}

const MobileCashEnableDisable = () => {
  const TOTPCode = useAppSelector((state) => state.auth.TOTPCode);
  const [showEnableQrDrawer, setShowEnableQrDrawer] = useState<boolean>();
  const [showEnableCodeDrawer, setShowEnableCodeDrawer] = useState<boolean>();
  const [showDisableCodeDrawer, setShowDisableCodeDrawer] = useState<boolean>();
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<FormType>();

  const {
    loading: loadingIsTwoFAActive,
    value: isTwoFAActive,
    retry: retryIsTwoFAActive,
  } = useMessagedAsyncRetry(
    async () => {
      const state = await userConfigurationApis.getAuthenticatorTotpStatusWithCancellationTokenGet("1");
      return state.authenticatorTotpEnabled;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [TOTPCode],
    { useProgress: true },
  );

  const [{ loading: loadingQrCodeUrl, value: qrCodeUrl }, getQrCodeUrl] = useMessagedAsyncFn(
    async () => {
      const qr = await userConfigurationApis.getAuthenticatorQrCodeUriWithCancellationTokenGet("1");
      return qr.qrCodeUri;
    },
    [],
    undefined,
    { useProgress: true },
  );

  const [{ loading: loadingEnable }, handleEnable] = useMessagedAsyncFn(
    async (values: FormType) => {
      await userConfigurationApis.enableAuthenticatorTotpWithRequestModelWithCancellationTokenPost({
        code: values.googleAuthCode,
      });
      retryIsTwoFAActive();
      form.resetFields();
      setShowEnableCodeDrawer(false);
    },
    [form, retryIsTwoFAActive],
  );

  const [{ loading: loadingDisable }, handleDisable] = useMessagedAsyncFn(
    async (values: FormType) => {
      await userConfigurationApis.disableAuthenticatorTotpWithRequestModelWithCancellationTokenPost({
        code: values.googleAuthCode,
      });
      retryIsTwoFAActive();
      form.resetFields();
      setShowDisableCodeDrawer(false);
    },
    [form, retryIsTwoFAActive],
  );

  return (
    <>
      <LegacyCard.Ant>
        <Space className={"tw:w-full"} size={"large"} orientation={"vertical"}>
          <Space className={"tw:w-full"}>
            <SafetyOutlined width={16} />
            <Typography.Title level={5} className={"tw:m-0!"}>
              {t("user.2fa.twoStepPasswordSettlement")}
            </Typography.Title>
          </Space>
          {isTwoFAActive ? (
            <Button onClick={() => setShowDisableCodeDrawer(true)} loading={loadingIsTwoFAActive}>
              <Space>
                <UnlockOutlined width={14} className={"color--danger"} />
                <Typography.Text className={"color--danger"}>{t("common.deactivate")}</Typography.Text>
              </Space>
            </Button>
          ) : (
            <Button
              onClick={async () => {
                setShowEnableQrDrawer(true);
                await getQrCodeUrl();
              }}
              loading={loadingIsTwoFAActive}
            >
              <Space>
                <LockOutlined width={14} />
                {t("common.activate")}
              </Space>
            </Button>
          )}
        </Space>
      </LegacyCard.Ant>

      <Modal
        title={t("user.2fa.modal.activate2fa")}
        open={showEnableQrDrawer}
        okText={t("user.2fa.modal.nextStep")}
        onOk={() => {
          setShowEnableQrDrawer(false);
          setShowEnableCodeDrawer(true);
        }}
        okButtonProps={{ loading: loadingQrCodeUrl }}
        cancelText={t("user.2fa.modal.close")}
        onCancel={() => setShowEnableQrDrawer(false)}
      >
        <Space orientation={"vertical"}>
          <Space size={"large"}>
            <Password className={"color--gray500"} width={"64px"} />
            <GoogleLogo className={"color--gray500"} width={"64px"} />
          </Space>
          <Typography.Text type={"secondary"}>{t("user.2fa.modal.googleAuthDesc")}</Typography.Text>
          <QRCode status={loadingQrCodeUrl ? "loading" : undefined} iconSize={134} value={qrCodeUrl || ""} />
        </Space>
      </Modal>

      <Modal
        title={t("user.2fa.modal.activate2fa")}
        open={showEnableCodeDrawer}
        okText={t("common.activate")}
        onOk={() => form.submit()}
        okButtonProps={{ loading: loadingEnable }}
        cancelText={t("back")}
        onCancel={() => setShowEnableCodeDrawer(false)}
      >
        <Space size={"large"}>
          <Password className={"color--gray500"} width={"64px"} />
          <GoogleLogo className={"color--gray500"} width={"64px"} />
        </Space>
        <Space orientation={"vertical"}>
          <Typography.Text type={"secondary"}>{t("user.2fa.modal.paypingCodeDesc")}</Typography.Text>
          <LegacyForm form={form} onFinish={handleEnable} layout={"vertical"}>
            <LegacyForm.Item label={t("user.2fa.modal.code")} name="googleAuthCode" rules={[{ required: true }]}>
              <Input.Password addonBefore={<Key width={"14px"} />} />
            </LegacyForm.Item>
          </LegacyForm>
        </Space>
      </Modal>

      <Modal
        title={t("user.2fa.disableLoginTwoStepPassword")}
        open={showDisableCodeDrawer}
        okText={t("common.deactivate")}
        onOk={() => form.submit()}
        okButtonProps={{ loading: loadingDisable }}
        cancelText={t("cancel")}
        onCancel={() => setShowDisableCodeDrawer(false)}
      >
        <Space size={"large"}>
          <Password className={"color--gray500"} width={"64px"} />
          <GoogleLogo className={"color--gray500"} width={"64px"} />
        </Space>
        <Space orientation={"vertical"}>
          <Typography.Text type={"secondary"}>{t("user.2fa.modal.paypingCodeDesc")}</Typography.Text>
          <LegacyForm form={form} onFinish={handleDisable} layout={"vertical"}>
            <LegacyForm.Item label={t("user.2fa.modal.code")} name="googleAuthCode" rules={[{ required: true }]}>
              <Input.Password addonBefore={<Key width={"14px"} />} />
            </LegacyForm.Item>
          </LegacyForm>
        </Space>
      </Modal>
    </>
  );
};

export default MobileCashEnableDisable;
